import { FC, useEffect } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { MdRefresh, MdSearch, MdSettings } from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { FiltroItensPendentesType, useMontagemCartelas } from './card-assembly.hook'
import { MontagemCartelasTable } from '../../../Components/HCPCardAssembly/card-assembly-table.component'
import { TiragemConfigDialog } from '../../../Components/HCPCardAssembly/print-run-config-dialog.component'

const MontagemCartelas: FC = () => {
  const {
    grupos,
    loading,
    lastUpdated,
    filtroBusca,
    setFiltroBusca,
    filtroItensPendentes,
    setFiltroItensPendentes,
    fetchDados,
    tiragemConfig,
    produtosDisponiveis,
    configDialogOpen,
    setConfigDialogOpen,
    salvarConfiguracao,
  } = useMontagemCartelas()

  const FILTRO_PENDENTES_LABELS: Record<FiltroItensPendentesType, string> = {
    todos: 'Todos',
    completos: 'Todas artes aprovadas',
    com_pendencias: 'Com pendências',
  }

  useEffect(() => { fetchDados() }, [fetchDados])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#f5f7fb', pt: 3, pb: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Montagem de Cartelas</Typography>
            <Typography variant="body2" color="text.secondary">
              Fluxo 2 - Artes aprovadas agrupadas por produto
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MdSettings size={16} />}
              onClick={() => setConfigDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Configurar tiragens
            </Button>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={loading ? <CircularProgress size={14} /> : <MdRefresh size={16} />}
                onClick={fetchDados}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Atualizar
              </Button>
              {lastUpdated && (
                <Typography variant="caption" color="text.disabled">
                  Atualizado as {format(lastUpdated, 'HH:mm', { locale: ptBR })}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1 }}>
            Aguardando OS
          </Typography>

          <TextField
            size="small"
            placeholder="Buscar por produto..."
            value={filtroBusca}
            onChange={(e) => setFiltroBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><MdSearch size={18} color="#9e9e9e" /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, bgcolor: '#fff', borderRadius: 1, mb: 0.5 }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.5, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>Pedido:</Typography>
        {(['todos', 'completos', 'com_pendencias'] as FiltroItensPendentesType[]).map((v) => (
          <Chip
            key={v}
            label={FILTRO_PENDENTES_LABELS[v]}
            size="small"
            onClick={() => setFiltroItensPendentes(v)}
            sx={{
              fontSize: '0.72rem',
              height: 22,
              cursor: 'pointer',
              fontWeight: filtroItensPendentes === v ? 700 : 400,
              bgcolor: filtroItensPendentes === v
                ? (v === 'completos' ? '#2e7d32' : v === 'com_pendencias' ? '#e65100' : '#1565c0')
                : 'rgba(0,0,0,0.06)',
              color: filtroItensPendentes === v ? '#fff' : 'text.secondary',
              '&:hover': { opacity: 0.85 },
            }}
          />
        ))}
      </Box>

      <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', mt: 1 }}>
        {loading && grupos.length === 0 ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <MontagemCartelasTable grupos={grupos} loading={loading} />
        )}
      </Box>

      <TiragemConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        produtos={produtosDisponiveis}
        config={tiragemConfig}
        onSave={salvarConfiguracao}
      />
    </Box>
  )
}

export default MontagemCartelas
